import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, untracked } from '@angular/core';

import { FilterBarVisibilityService } from './filter-bar-route';
import { RealtimeStatusService } from '../../core/realtime/realtime-status.service';
import { ActivePageDataService } from '../../core/time/active-page-data.service';
import { FreshnessIndicatorComponent } from '../../ui/freshness/freshness-indicator.component';
import { FilterOptionsService } from '../../core/filters/filter-options.service';
import { FilterScopeController } from '../../core/filters/filter-scope.service';
import {
  OPEN_SET_DIMENSIONS,
  type DateRangeSelection,
  type OpenSetDimension,
  type StatusValue,
} from '../../core/filters/filter-state.model';
import { FaIconComponent, faRotateLeft } from '../../ui/icons/app-icons.registry';
import { DateRangePickerComponent } from './date-range-picker.component';
import { DEFAULT_DISABLED_REASON, SearchableSelectComponent } from './searchable-select.component';
import { StatusMultiSelectComponent } from './status-multi-select.component';
import { UserFilterInputComponent } from './user-filter-input.component';

/**
 * The persistent filter bar (FR-001). It composes one control per dimension —
 * never a god-component (Art. XI-A) — and owns no state of its own: every
 * control reads a draft signal from {@link FilterScopeController} and writes
 * back through it, so the shared state outlives any bar instance and survives
 * navigation (R-011).
 *
 * The bar injects the **controller** (the internal writable surface), not the
 * published `FilterScopeService` port. Subscribed views read the port; the bar
 * is the only writer.
 *
 * The client selector is deliberately absent for client-portal roles — rendered
 * under `@if`, never CSS-hidden (Art. XI-C). That is UX only: the API's `403`
 * on `GET /api/v1/clients` is the enforcement (FR-005, SC-007).
 */
@Component({
  selector: 'app-filter-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FreshnessIndicatorComponent,
    FaIconComponent,
    DateRangePickerComponent,
    SearchableSelectComponent,
    StatusMultiSelectComponent,
    UserFilterInputComponent,
  ],
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss',
})
export class FilterBarComponent {
  protected readonly filters = inject(FilterScopeController);
  private readonly options = inject(FilterOptionsService);

  protected readonly resetIcon = faRotateLeft;

  protected readonly dateRange = this.filters.draftDateRange;
  protected readonly app = this.filters.draftApp;
  protected readonly model = this.filters.draftModel;
  protected readonly provider = this.filters.draftProvider;
  protected readonly appUnspecified = this.filters.draftAppUnspecified;
  protected readonly modelUnspecified = this.filters.draftModelUnspecified;
  protected readonly providerUnspecified = this.filters.draftProviderUnspecified;
  protected readonly user = this.filters.draftUser;

  /**
   * GH-164 C-4: the label of the blank-segment choice the open-set selectors offer when the list
   * observed it. Rendered italic, and committed as `{dim}State=unspecified` — never as this text.
   */
  protected readonly UNSPECIFIED_LABEL = 'Unspecified';

  protected readonly DEFAULT_DISABLED_REASON = DEFAULT_DISABLED_REASON;
  protected readonly status = this.filters.draftStatus;
  protected readonly isInternalRole = this.filters.isInternalRole;
  /** Bumped on Reset and the FR-019 session clear; the picker discards its local Custom draft on it. */
  protected readonly resetRevision = this.filters.resetRevision;

  protected readonly appOptions = this.options.optionsFor('app');
  protected readonly modelOptions = this.options.optionsFor('model');
  protected readonly providerOptions = this.options.optionsFor('provider');
  protected readonly clientOptions = this.options.optionsFor('client');

  /**
   * AITH-45 U3 (obligation 6): a destination may remove a selector whose values it cannot obtain.
   * The absence IS the declaration — the page states it in prose alongside — which is honest in a
   * way three permanently inert dropdowns are not.
   *
   * GH-34 §1 (D-3/D-5) adds the second treatment beside it. `hidden` and `disabled` state two
   * different facts and are never interchangeable: see `filter-bar-route.ts`.
   */
  private readonly filterBarRoute = inject(FilterBarVisibilityService);
  private readonly hiddenDimensions = this.filterBarRoute.hiddenDimensions;
  private readonly disabledDimensions = this.filterBarRoute.disabledDimensions;


  constructor() {
    // GH-164 D-12: tell the options service which open-set selectors this route offers as OPERABLE,
    // so a staff caller with no client fetches the cross-client lists only where one can be used.
    // Cleared when the bar leaves the page: a bar that is not rendered offers nothing.
    effect(() => {
      const operable = new Set(
        OPEN_SET_DIMENSIONS.filter(
          (dimension) => !this.hiddenDimensions().has(dimension) && !this.disabledDimensions().has(dimension),
        ),
      );
      untracked(() => this.options.setCrossClientDemand(operable));
    });
    inject(DestroyRef).onDestroy(() => this.options.setCrossClientDemand(new Set()));
  }

  protected isDimensionHidden(dimension: string): boolean {
    return this.hiddenDimensions().has(dimension);
  }

  /**
   * Absence wins over inertness: a dimension declared in BOTH sets is not rendered at all, so it
   * can never be reported as disabled.
   *
   * That precedence is enforced by the template's nesting, not here — every `[disabled]` binding
   * sits inside its dimension's `@if (!isDimensionHidden(…))`, so this is never asked about a
   * hidden dimension. It was written with the conjunct spelled out again and the negative control
   * showed the conjunct could not be made to fail: removing it changed nothing, because the
   * template had already decided. An unfalsifiable guard is worse than no guard, so it is gone and
   * the property is asserted through the DOM instead (`filter-bar.component.spec.ts`), where
   * moving a binding out of its guard DOES turn the test red.
   */
  protected isDimensionDisabled(dimension: string): boolean {
    return this.disabledDimensions().has(dimension);
  }

  /**
   * GH-164 D-11: the reason a disabled dimension states, when the route declared one; otherwise
   * `undefined`, and the control keeps its own default ("Not available here yet").
   */
  protected disabledReasonFor(dimension: string): string | undefined {
    return this.filterBarRoute.disabledReasons().get(dimension);
  }

  /**
   * The disabled control's default value (GH-34 §1, D-8 R2).
   *
   * Deliberately **no count**, unlike {@link allClientsPlaceholder}: a cross-client observed-value
   * count is exactly what AITH-44 R-003 makes unobtainable, and that unobtainability is the reason
   * the control is inert in the first place. Rendering "(0)" here would state something false in
   * the one place the treatment is meant to be honest.
   */
  protected readonly OPEN_SET_PLACEHOLDER: Readonly<Record<OpenSetDimension, string>> = {
    app: 'All applications',
    model: 'All models',
    provider: 'All providers',
  };

  // AITH-45 D-08: the indicator moved here from the top bar.
  protected readonly activePageData = inject(ActivePageDataService);
  protected readonly realtimeStatus = inject(RealtimeStatusService);

  /**
   * "All clients (8)" — the directory size, per the design (AITH-45 U1).
   *
   * The count appears ONLY once the list has actually resolved. Rendering "(0)" while loading, or
   * after the list failed, would state something false about the estimate rather than saying
   * nothing — and `FilterOptionsService` settles a failed list as unavailable for the whole
   * session, so a wrong "(0)" would persist until reload.
   */
  protected readonly allClientsPlaceholder = computed(() => {
    const list = this.clientOptions();
    return list.status === 'success' && list.values.length > 0
      ? `All clients (${list.values.length})`
      : 'All clients';
  });

  /**
   * REMOVED 2026-09-19 — `openSetUnavailableMessage`, the "Select a client first" override.
   *
   * It existed to explain, on an **operable** app/model/provider selector, why its option list was
   * empty: AITH-44 R-003 makes cross-client observed values unobtainable for a staff caller with
   * no client selected. GH-34 §1 D-3 disables all three on every dashboard page, and a disabled
   * control's status line carries the reason it is disabled (D-8 R1) — which answers that question
   * better and earlier, before the reader tries. The branch became unreachable application-wide,
   * so it is removed here with the state it served rather than left silently dead (D-9).
   *
   * **FR-016 is narrowed, not weakened** (AITH-44 spec amendment, D-9): its loading / empty /
   * unavailable states survive in full for the `client` selector, whose list is still fetched and
   * whose control is still operable on `/clients/:clientCode` and `/exports`. That selector binds
   * no override and reads `searchable-select`'s own `'Options unavailable'` default.
   *
   * If a future route ever renders app/model/provider operable again, restore this override with
   * it — `app.routes.spec.ts` fails on a dashboard route that declares neither treatment, so that
   * decision cannot be made by omission.
   */

  /** The client selector applies a code and displays the client's name (contract §Schema). */
  protected readonly clientDisplayFor = (clientCode: string): string =>
    this.options.clientDisplayName(clientCode);

  protected onDateRangeChange(selection: DateRangeSelection): void {
    this.filters.setDateRange(selection);
  }

  protected onUnspecifiedChosen(dimension: OpenSetDimension): void {
    this.filters.selectUnspecified(dimension);
  }

  protected onOpenSetChange(dimension: OpenSetDimension, value: string | null): void {
    switch (dimension) {
      case 'app':
        this.filters.setApp(value);
        return;
      case 'model':
        this.filters.setModel(value);
        return;
      case 'provider':
        this.filters.setProvider(value);
        return;
    }
  }

  protected onClientChange(clientCode: string | null): void {
    this.filters.setClientCode(clientCode);
  }

  protected onUserCommit(value: string | null): void {
    this.filters.commitUser(value);
  }

  protected onStatusChange(status: readonly StatusValue[]): void {
    this.filters.setStatus(status);
  }

  protected reset(): void {
    this.filters.reset();
  }
}
